import { inject, Injectable } from '@angular/core';
import { addMinutes } from 'date-fns';
import { Draft, produce } from 'immer';
import { map, Observable } from 'rxjs';

import { CanvasElement, CanvasElementId, WrapperField } from '@blaze-runtime/engine-domain';
import { shiftUtcBetweenCalendarTimezones } from '@blaze-runtime/engine-util';
import { FormContextCalendar } from '@blaze-shared/elements-domain';
import { type ElementSchema, WorkflowSchema } from '@blaze-shared/schema-domain';
import { isEqual, type Nullish } from '@blaze-shared/shared-util';

import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { BaseChildrenHandlerService } from '../../canvas-elements/io-data/default-handlers/base-children-handler.service';
import {
  ComponentChildrenConfig,
  ComponentChildrenHandler,
  UpdateInputsCacheParams,
} from '../../canvas-elements/io-data/io-data.models';
import {
  FormRenderElementInstance,
  InputsCache,
} from '../../canvas-elements/models/render-element.models';
import { FormChildOutputService } from '../../services/forms/runtime/public-api';
import { isElementChildOf } from '../../utils/layout/relationship';

@Injectable({ providedIn: 'root' })
export class FormsChildrenHandlerService
  extends BaseChildrenHandlerService
  implements ComponentChildrenHandler
{
  private readonly formChildOutput = inject(FormChildOutputService);

  buildInputsCache(element: CanvasElement): InputsCache {
    const inputsCache = Object.entries(getWrapperRefs(element))
      .filter(([ref]) => WrapperField.isRefId(ref))
      .reduce(
        (acc, [ref, id]) => {
          acc[id] = this.schemaService.getColumnName(WrapperField.getValue(ref)) ?? false;
          return acc;
        },
        {} as Record<CanvasElementId, string | boolean>,
      );
    return inputsCache;
  }

  override updateInputsCache({
    inputsCache,
    inputElement,
    formElement,
    elements,
  }: UpdateInputsCacheParams): void {
    if (
      !inputsCache[inputElement.id] &&
      formElement &&
      isElementChildOf(elements, inputElement.id, formElement.id)
    ) {
      // legacy (pre wrapper-refs) forms
      if (!hasDataProps(inputElement)) {
        inputsCache[inputElement.id] = true;
      } else {
        const valueBinding = inputElement.props.data.find(
          (b) => b && b.dataSource?.id === formElement.id,
        );
        inputsCache[inputElement.id] =
          !valueBinding ||
          (this.schemaService.getColumnName(valueBinding.bindings?.[valueBinding.key]) ?? false);
      }
    }
  }

  connectChildren({
    container,
    childrenUpdates$,
    inputsCache,
    formInstance,
  }: ComponentChildrenConfig): Observable<void> {
    const instance = formInstance as unknown as FormRenderElementInstance;

    return childrenUpdates$.pipe(
      map(({ event, formElement, inputElement, formContext }) => {
        if (!this.formChildOutput.contributesToFormOutput(inputElement.type)) {
          return;
        }
        container = formElement as any;

        const modelField = inputsCache[event.elementId];
        if (typeof modelField === 'boolean') {
          return;
        }

        const dataValueField = this.getDataValueField(inputElement, event.data);

        const instanceSchema =
          instance.schema || this.getElementOutputSchema(formElement, 'change', formInstance);
        const { value: inputValue, triggerFormulasRefresh } = this.getChildValue({
          formInstance,
          dataValueField,
          modelField,
          eventData: event.data,
          formDataBindings: container.props.data,
          inputElement,
          instanceValue: instance.value,
          instanceSchema,
          formContext,
          preventLinksExtraction: true,
        });

        const {
          value: currentValue,
          converted,
          extracted,
        } = this.getChildValue({
          formInstance,
          dataValueField,
          modelField,
          eventData: instance.value?.[modelField],
          formDataBindings: container.props.data,
          inputElement,
          instanceValue: instance.value,
          instanceSchema,
          formContext,
          preventLinksExtraction: true,
        });

        /** Don't do anything if the values are equal and the current value was not converted */
        if (isEqual(currentValue, inputValue) && !converted && !extracted) {
          return;
        }

        instance.value = produce(instance.value ?? {}, (draft: any) => {
          draft[modelField] = inputValue;

          // Handle changes for Calendar context
          if (formContext?.type === 'calendar' && !event.isInitialData) {
            handleCalendarFormChangesMutably(
              draft,
              formContext,
              event,
              modelField,
              inputValue,
              instance.value,
            );
          }
        });
        instance.valueUpdated(modelField, inputValue);
        instance.validateValue();
        this.updateDataContainerOutput(
          container,
          inputElement,
          instance,
          event,
          triggerFormulasRefresh,
        );
      }),
    );
  }
}

export function handleCalendarFormChangesMutably(
  formValueDraft: Draft<any>,
  formContext: FormContextCalendar,
  event: { data: any; schema?: Nullish<ElementSchema | WorkflowSchema> },
  modifiedField: string,
  updatedValue: any,
  previousFormValue: any,
): void {
  // Handle start column modification
  // If end date missing or selected start date is after end date -> set a correct end date
  if (
    formContext.defaultDurationMinutes !== undefined &&
    formContext.startColumn &&
    formContext.endColumn &&
    modifiedField === formContext.startColumn
  ) {
    const startValue = updatedValue;
    const endValue = formValueDraft[formContext.endColumn];
    if (!endValue || (startValue && new Date(startValue) >= new Date(endValue))) {
      formValueDraft[formContext.endColumn] = addMinutes(
        new Date(startValue),
        formContext.defaultDurationMinutes,
      ).toISOString();
    }
  }

  // Handle calendar change (together with linked Timezone)
  if (
    formContext.calendarColumn &&
    formContext.timeZoneColumn &&
    event.schema &&
    modifiedField === formContext.calendarColumn
  ) {
    const linkedTimeZoneColumn = event.schema.columns.find(
      (column) => column.format?.formatType === 'TimeZone',
    );

    // Calendar was changed and linked time zone is present
    if (linkedTimeZoneColumn) {
      const previousTimeZone = previousFormValue[formContext.timeZoneColumn];
      const newTimeZone = event.data[linkedTimeZoneColumn.name];
      formValueDraft[formContext.timeZoneColumn] = newTimeZone;

      // If timezone was changed -> shift the dates so the displayed wall-clock time doesn't move
      if (previousTimeZone !== newTimeZone) {
        for (const column of [formContext.startColumn, formContext.endColumn]) {
          if (column && formValueDraft[column]) {
            formValueDraft[column] = shiftUtcBetweenCalendarTimezones(
              new Date(formValueDraft[column]),
              previousTimeZone,
              newTimeZone,
            ).toISOString();
          }
        }
      }
    }
  }
}
